import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";

import type { ShareToChatSubject } from "@xop/dashboard-shared";

import {
  appDeepLinkUrl,
  buildMailtoHref,
  canNativeShare,
  copyToClipboard,
  triggerMailto,
  triggerNativeShare,
} from "./appShareLinks.js";
import type { ShareMenuItem } from "./ShareMenu.js";

/**
 * The canonical Share actions for an **in-app staff record** (CRM prospect /
 * customer / sales lead), as `ShareMenuItem[]`:
 *
 *  - **Share via email** — a `mailto:` draft opening the default mail app,
 *    carrying the `xopstaff://` desktop deep-link.
 *  - **Share to chat** — posts a non-monetary summary + in-app deep-link into
 *    the internal staff chat (the caller owns the dialog → `onShareToChat`).
 *  - **Copy link** — the `xopstaff://` desktop deep-link on the clipboard, to
 *    any other app (Slack/Teams/Messages …).
 *  - **Share to app…** — the native OS share sheet, appended ONLY where the
 *    platform offers one (mobile web + some desktop browsers; absent in the
 *    Tauri desktop shell, where Copy link covers it).
 *
 * Every link is built from the same {@link ShareToChatSubject} that drives the
 * chat share, so the chat deep-link and the emailed/copied URL can never drift.
 * The subject's `summaryLines` are already non-monetary (enforced by the
 * per-entity adapters in `crm/lib/shareSubject.ts` + `pages/sales/leads/
 * shareSubject.ts`), so the email body stays inside the money wall.
 *
 * `notify` is the surface's own toast (the CRM sub-app and the dashboard use
 * different toast providers), called on a successful copy. Relative imports
 * only so it typechecks under both tsconfigs.
 */
export interface EntityShareItemsArgs {
  subject: ShareToChatSubject;
  onShareToChat: () => void;
  /** Surface-local toast, fired after a successful "Copy link". */
  notify?: (msg: { title: string; description?: string }) => void;
}

/** "Copy link" + (when supported) "Share to app…" — the link-only actions,
 * exported on their own so a surface with a bespoke email flow (the sales
 * lead's branded-PDF email) can append them to its existing menu instead of
 * taking the full trio. */
export function linkShareItems({ subject, notify }: Omit<EntityShareItemsArgs, "onShareToChat">): ShareMenuItem[] {
  const url = appDeepLinkUrl(subject.deepLinkPath);
  const items: ShareMenuItem[] = [
    {
      key: "copy",
      label: "Copy link",
      icon: <ContentCopyRoundedIcon fontSize="small" />,
      testId: "share-menu-copy-link",
      onClick: () => {
        void copyToClipboard(url).then((ok) => {
          if (ok)
            notify?.({ title: "Link copied", description: `${subject.kind} link copied to clipboard.` });
        });
      },
    },
  ];
  if (canNativeShare()) {
    items.push({
      key: "native",
      label: "Share to app…",
      icon: <IosShareRoundedIcon fontSize="small" />,
      testId: "share-menu-native",
      onClick: () => {
        const text = [subject.kind, subject.title].filter(Boolean).join(": ");
        void triggerNativeShare({ title: subject.title, text, url });
      },
    });
  }
  return items;
}

/** `mailto:` "Share via email" item carrying the `xopstaff://` desktop link. */
export function emailShareItem(subject: ShareToChatSubject): ShareMenuItem {
  return {
    key: "email",
    label: "Share via email",
    icon: <EmailOutlinedIcon fontSize="small" />,
    testId: "share-menu-email",
    onClick: () => triggerMailto(entityMailtoHref(subject)),
  };
}

/** Build the `mailto:` href for an entity: `${kind}: ${title}` subject, body =
 * a one-line intro + the non-monetary summary + the `xopstaff://` desktop link. */
export function entityMailtoHref(subject: ShareToChatSubject): string {
  const url = appDeepLinkUrl(subject.deepLinkPath);
  const title = subject.title.trim() || "record";
  const summary = subject.summaryLines.map((l) => l.trim()).filter(Boolean).join(" · ");
  const lines = [`Sharing a ${subject.kind.toLowerCase()}: ${title}`];
  if (summary) lines.push(summary);
  lines.push("", url);
  return buildMailtoHref({ subject: `${subject.kind}: ${title}`, body: lines.join("\n") });
}

/** The full canonical trio (+ native when supported): email · chat · copy link.
 * Used by surfaces with no bespoke email flow (CRM prospect + customer). */
export function buildEntityShareItems({ subject, onShareToChat, notify }: EntityShareItemsArgs): ShareMenuItem[] {
  return [
    emailShareItem(subject),
    {
      key: "chat",
      label: "Share to chat",
      icon: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
      testId: "share-menu-chat",
      onClick: onShareToChat,
    },
    ...linkShareItems({ subject, ...(notify ? { notify } : {}) }),
  ];
}
